import "./SnapWidgetPrivacyPolicy.css";

const updatedAt = "July 11, 2026";

const sections = [
  {
    title: "1. Our Services",
    body: [
      "Privacy Screen Guard is a mobile application designed to help reduce shoulder surfing and protect screen privacy in public or shared environments.",
      "The Services are not intended for use in jurisdictions where their use would violate local law or require us to register in that jurisdiction. You are responsible for complying with local laws when you use the Services."
    ]
  },
  {
    title: "2. Intellectual Property Rights",
    body: [
      "BlackDoor owns or licenses the software, designs, code, text, graphics, trademarks, service marks, and other materials used in Privacy Screen Guard.",
      "Subject to these Terms, we grant you a limited, revocable, non-exclusive, non-transferable license to install and use Privacy Screen Guard for personal, non-commercial purposes.",
      "You may not copy, reproduce, distribute, sell, license, modify, reverse engineer, decompile, or exploit any part of the Services except as expressly allowed by these Terms or by law."
    ]
  },
  {
    title: "3. User Representations",
    body: [
      "By using Privacy Screen Guard, you represent that you have legal capacity to agree to these Terms, that your use will comply with applicable law, and that any information you provide to us is true, current, and complete.",
      "You agree not to access or use the Services through bots, scripts, automated systems, or other non-human means."
    ]
  },
  {
    title: "4. App Permissions",
    body: [
      "Privacy Screen Guard may require device permissions such as overlay/display access, notification access, camera access, or other permissions needed to provide privacy screen functionality.",
      "You are responsible for reviewing permissions before enabling them. Some features may not work if required permissions are disabled or revoked."
    ]
  },
  {
    title: "5. Smart Privacy Features",
    body: [
      "Peek-a-Boo, Look Away, and similar smart privacy features use on-device camera and AI/ML analysis to detect privacy-related signals, such as multiple visible faces, the registered viewer being present, or the registered viewer appearing to look away from the screen.",
      "Smart privacy features are designed to assist screen privacy, but they are not a substitute for your own judgment and may be inaccurate, delayed, unavailable, or affected by lighting, camera position, device performance, face angle, eyewear, masks, other people in frame, operating system limits, or permission changes.",
      "You are responsible for using these features lawfully and respectfully, including where camera use, biometric-like templates, workplace rules, consent, or recording-related laws may apply."
    ]
  },
  {
    title: "6. Products and Paid Features",
    body: [
      "Privacy Screen Guard may offer paid access, paid features, or other one-time purchases. Products and paid features are subject to availability and may change over time.",
      "We may modify, suspend, or discontinue any feature at any time, including free or paid features, where permitted by law."
    ]
  },
  {
    title: "7. Purchases and Payment",
    body: [
      "You agree to provide current, complete, and accurate purchase and account information for purchases made through the Services.",
      "Payments are one-time charges unless a checkout screen expressly states otherwise. You authorize Google Play or the applicable payment provider to charge your selected payment method for the purchase price, taxes, and other charges shown at checkout.",
      "Payments are processed by Google Play or another payment provider and are also subject to that provider's terms, billing rules, and mandatory refund policies.",
      "We may verify purchases with Google Play or another payment provider before unlocking paid features. Verification may use purchase tokens, product IDs, acknowledgement status, restore status, and an app-generated user identifier to confirm that the correct product was paid for."
    ]
  },
  {
    title: "8. No Subscriptions",
    body: [
      "Privacy Screen Guard payments are currently one-time purchases. We do not currently offer recurring subscriptions for Privacy Screen Guard.",
      "If we introduce subscriptions in the future, the checkout screen and these Terms will be updated to explain renewal, billing, and cancellation terms before you purchase."
    ]
  },
  {
    title: "9. Refunds",
    body: [
      "All Privacy Screen Guard payments are final and non-refundable except where a refund is required by applicable law or mandatory Google Play or payment provider policy.",
      "Because purchases are one-time payments that unlock digital app functionality, you acknowledge that you may lose refund eligibility after purchase to the fullest extent permitted by law.",
      "Refund requests submitted through Google Play or another payment provider are handled under that provider's rules.",
      "If a purchase is refunded, reversed, voided, charged back, or cannot be verified, we may revoke or suspend the related paid access. You may use Restore Purchase in the app to restore eligible purchases on a compatible device using the same store account."
    ]
  },
  {
    title: "10. Prohibited Activities",
    body: [
      "You may not use the Services for unlawful, unauthorized, harmful, abusive, fraudulent, automated, or commercial purposes not approved by us.",
      "You may not interfere with security features, bypass payment controls, impersonate others, collect user data without permission, upload malicious code, reverse engineer the Services, or use Privacy Screen Guard to build or support a competing product.",
      "You may not sell, rent, sublicense, transfer, or commercially exploit your access to Privacy Screen Guard unless we expressly approve it in writing."
    ]
  },
  {
    title: "11. Mobile Application License",
    body: [
      "If you access the Services through the Privacy Screen Guard app, we grant you a limited, revocable, non-transferable license to install and use the app on devices you own or control.",
      "If you obtained the app through Google Play, Google Play's terms also apply. If there is a conflict between these Terms and mandatory app store terms, the mandatory app store terms will control only to the extent of the conflict."
    ]
  },
  {
    title: "12. Third-Party Services",
    body: [
      "The Services may rely on third-party services such as app stores, payment providers, analytics providers, crash reporting tools, operating system services, or device settings.",
      "Your relationship with third-party providers is governed by your agreements with them. We are not responsible for third-party services, content, policies, outages, or billing decisions."
    ]
  },
  {
    title: "13. Privacy Policy",
    body: [
      "Your use of the Services is also governed by our Privacy Policy, which is incorporated into these Terms.",
      "The Services are operated from India. If you access the Services from another region, you understand that your information may be transferred to and processed in India or other locations where our service providers operate."
    ],
    link: {
      label: "Review the Privacy Screen Guard Privacy Policy",
      href: "/apps/privacy-screen-guard/privacy-policy"
    }
  },
  {
    title: "14. Services Management",
    body: [
      "We may monitor the Services for violations, restrict access, suspend features, remove burdensome files or data, and take legal action where appropriate.",
      "We may manage the Services in any manner designed to protect users, our rights, our property, payment systems, and the proper functioning of Privacy Screen Guard."
    ]
  },
  {
    title: "15. Term and Termination",
    body: [
      "These Terms remain in effect while you use the Services. We may deny access, suspend, terminate, or restrict your use of the Services at any time if we believe you violated these Terms or applicable law.",
      "Termination does not entitle you to a refund unless a refund is required by applicable law or mandatory payment provider policy."
    ]
  },
  {
    title: "16. Modifications and Interruptions",
    body: [
      "We may change, suspend, discontinue, or remove features or content at any time. We are not liable for modification, price changes, suspension, downtime, or discontinuance of the Services.",
      "We do not guarantee that the Services will always be available, uninterrupted, secure, or error-free."
    ]
  },
  {
    title: "17. Governing Law",
    body: [
      "These Terms are governed by the laws of India. BlackDoor and you consent to the jurisdiction of the courts of India for disputes that may arise in connection with these Terms, subject to the dispute resolution provisions below."
    ]
  },
  {
    title: "18. Dispute Resolution",
    body: [
      "Before starting arbitration or court proceedings, the parties agree to attempt informal negotiations for at least sixty (60) days after written notice of a dispute.",
      "Any dispute arising out of or in connection with these Terms will be referred to and finally resolved by arbitration under the rules of the International Commercial Arbitration Court under the European Arbitration Chamber. The number of arbitrators will be two (2), the seat of arbitration will be Bangalore, India, and the language will be English.",
      "Arbitration will be limited to the dispute between the parties individually. Class actions and representative proceedings are not permitted to the fullest extent allowed by law."
    ]
  },
  {
    title: "19. Disclaimer",
    body: [
      "THE SERVICES ARE PROVIDED ON AN AS-IS AND AS-AVAILABLE BASIS. TO THE FULLEST EXTENT PERMITTED BY LAW, WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.",
      "Privacy Screen Guard is designed to support screen privacy, but we do not guarantee that it will prevent every viewing, capture, accessibility, operating system, overlay, camera, AI detection, face tracking, anomaly detection, or device-level privacy risk."
    ]
  },
  {
    title: "20. Limitations of Liability",
    body: [
      "TO THE FULLEST EXTENT PERMITTED BY LAW, BLACKDOOR, ITS DIRECTORS, EMPLOYEES, AND AGENTS WILL NOT BE LIABLE FOR INDIRECT, CONSEQUENTIAL, EXEMPLARY, INCIDENTAL, SPECIAL, OR PUNITIVE DAMAGES, INCLUDING LOST PROFIT, LOST REVENUE, LOSS OF DATA, OR PRIVACY INCIDENTS OUTSIDE OUR REASONABLE CONTROL.",
      "Our total liability for any claim will be limited to the amount paid by you for Privacy Screen Guard during the three (3) month period before the cause of action arose, unless applicable law requires otherwise."
    ]
  },
  {
    title: "21. Indemnification",
    body: [
      "You agree to defend, indemnify, and hold BlackDoor and its affiliates, officers, agents, partners, and employees harmless from claims, damages, losses, liabilities, and expenses arising from your use of the Services, your violation of these Terms, your violation of third-party rights, or harmful acts toward another person."
    ]
  },
  {
    title: "22. Electronic Communications",
    body: [
      "Using the Services, sending emails, and completing online forms constitute electronic communications. You consent to receive electronic communications and agree that electronic notices, disclosures, signatures, contracts, and records satisfy legal writing requirements."
    ]
  },
  {
    title: "23. Miscellaneous",
    body: [
      "These Terms and any policies posted by us constitute the entire agreement between you and BlackDoor regarding the Services.",
      "If any provision is unlawful, void, or unenforceable, that provision will be severed and the remaining provisions will remain valid and enforceable.",
      "No joint venture, partnership, employment, or agency relationship is created by these Terms or your use of the Services."
    ]
  },
  {
    title: "24. Contact Us",
    body: [
      "To resolve a complaint or receive more information about the Services, contact BlackDoor at support@blackdoorapps.com or by post at BlackDoor, Kalyannagar, North 24 Parganas, Kolkata, West Bengal 700112, India."
    ]
  }
];

function PrivacyScreenGuardTermsAndConditions() {
  return (
    <main className="snap-policy-page">
      <section className="snap-policy-hero">
        <a className="snap-policy-brand" href="/" aria-label="BlackDoor home">
          BlackDoor
        </a>
        <p className="snap-policy-kicker">Privacy Screen Guard</p>
        <h1>Terms and Conditions</h1>
        <p className="snap-policy-updated">Last updated {updatedAt}</p>
        <p className="snap-policy-summary">
          These Terms and Conditions govern your access to and use of Privacy Screen Guard and related BlackDoor services.
        </p>
      </section>

      <section className="snap-policy-content" aria-label="Terms and conditions content">
        <div className="snap-policy-card snap-policy-intro">
          <h2>Agreement to Our Legal Terms</h2>
          <p>
            By accessing or using Privacy Screen Guard, you agree to these Terms. If you do not agree, you must stop using the Services immediately.
          </p>
          <p>
            Privacy Screen Guard payments are one-time purchases and are non-refundable except where required by applicable law or mandatory payment provider policy.
          </p>
          <p>
            The Services are intended for users who are at least 18 years old. Persons under 18 are not permitted to use or register for the Services.
          </p>
        </div>

        {sections.map((section) => (
          <article className="snap-policy-card" key={section.title}>
            <h2>{section.title}</h2>
            {section.body.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
            {section.link && (
              <p>
                <a href={section.link.href}>{section.link.label}</a>
              </p>
            )}
          </article>
        ))}
      </section>
    </main>
  );
}

export default PrivacyScreenGuardTermsAndConditions;
